import { createFileRoute, useNavigate, useLocation, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { EmailVerifyBanner } from "@/components/EmailVerifyBanner";
import { LiveLocationConsentDialog } from "@/components/LiveLocationConsentDialog";
import { FirstRunLocationConsent } from "@/components/FirstRunLocationConsent";
import { OnDutyLocationReporter } from "@/components/OnDutyLocationReporter";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMfaGate } from "@/hooks/useMfaGate";
import { requiredFlagForPath } from "@/lib/feature-routes";
import { isGuard, isGuardBlockedPath } from "@/lib/roles";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated")({
  // The Supabase session lives in localStorage, which the server can't read.
  // Rendering this subtree client-side only avoids a login bounce on hard refresh.
  ssr: false,
  // Route-level guard. Runs before any child route component renders, so
  // protected pages can never flash for unauthenticated users.
  beforeLoad: async ({ location }) => {
    const { data } = await supabase.auth.getSession();
    if (!data.session) {
      // location.href is already a string ("/manager?x=1"). Do NOT encode it —
      // the router encodes search params, and double-encoding makes the
      // login page discard `next` as unsafe.
      throw redirect({ to: "/login", search: { next: location.href } as never });
    }
  },
  component: AuthenticatedLayout,
});

function AuthScreen({ label }: { label: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-primary" />
        {label}
      </div>
    </div>
  );
}

function AuthenticatedLayout() {
  const { session, user, loading, membershipsLoading, memberships, isPlatformAdmin, currentRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const flags = useFeatureFlags();
  const mfa = useMfaGate();
  // null = not loaded yet, so the banner never flashes before we know.
  const [profileVerified, setProfileVerified] = useState<boolean | null>(null);

  const resolving = loading || (session && membershipsLoading);

  // OAuth providers (Google) already prove ownership of the address, so those
  // users must never see the verify nag. Stamp the profile once so the state
  // persists across devices/sessions.
  const oauthVerified = !!user?.identities?.some((i) => i.provider !== "email");

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      if (oauthVerified) {
        if (!cancelled) setProfileVerified(true);
        await supabase
          .from("profiles")
          .update({ email_verified_at: new Date().toISOString() } as never)
          .eq("id", user.id)
          .is("email_verified_at", null);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("email_verified_at")
        .eq("id", user.id)
        .maybeSingle();
      if (!cancelled) {
        setProfileVerified(!!(data as { email_verified_at?: string | null } | null)?.email_verified_at);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, oauthVerified]);



  useEffect(() => {
    if (resolving) return;
    if (!session) {
      navigate({ to: "/login", replace: true });
    } else if (memberships.length === 0 && !isPlatformAdmin) {
      navigate({ to: "/onboarding", replace: true });
    } else {
      // Resume self-serve onboarding for owners who dropped off mid-flow.
      const owned = memberships.find(m => m.role === "org_owner");
      if (owned && owned.organizations?.onboarding_completed === false) {
        navigate({ to: "/onboarding", replace: true });
      }
    }
  }, [resolving, session, memberships, isPlatformAdmin, navigate]);

  // Feature-flag route guard
  useEffect(() => {
    if (!flags.loaded || resolving) return;
    const required = requiredFlagForPath(location.pathname);
    if (required && flags[required] === false) {
      toast.info("That module is disabled for this organization.");
      navigate({ to: "/home", replace: true });
    }
  }, [flags, location.pathname, resolving, navigate]);

  // Guard (officer) route guard: management surfaces are not reachable by URL.
  // Database RLS remains authoritative — this only prevents landing on them.
  useEffect(() => {
    if (resolving || isPlatformAdmin) return;
    if (!isGuard(currentRole)) return;
    if (!isGuardBlockedPath(location.pathname)) return;
    toast.info("You don't have access to that area.");
    navigate({ to: "/home", replace: true });
  }, [resolving, isPlatformAdmin, currentRole, location.pathname, navigate]);

  // MFA enforcement: if role requires 2FA and user isn't enrolled / hasn't
  // verified this session, force them to /settings/security.
  useEffect(() => {
    if (!mfa.loaded || resolving) return;
    if (!mfa.needsEnroll && !mfa.needsVerify) return;
    if (location.pathname.startsWith("/settings/security")) return;
    toast.warning(
      mfa.needsEnroll
        ? "Your role requires two-factor authentication. Please enroll to continue."
        : "Please verify with your authenticator to continue.",
    );
    navigate({ to: "/settings/security", replace: true });
  }, [mfa.loaded, mfa.needsEnroll, mfa.needsVerify, location.pathname, resolving, navigate]);

  if (resolving) return <AuthScreen label="Restoring your session…" />;
  if (!session) return <AuthScreen label="Redirecting to sign in…" />;
  if (memberships.length === 0 && !isPlatformAdmin) return <AuthScreen label="Setting up your workspace…" />;

  // Soft verification: signup no longer blocks on the email link, so
  // email_confirmed_at is always set. Our own profiles.email_verified_at is
  // the real signal, stamped when the user completes the code check.
  const needsVerify = !!user?.email && profileVerified === false && !isPlatformAdmin;

  return (
    <>
      {needsVerify && (
        <EmailVerifyBanner email={user!.email!} onVerified={() => setProfileVerified(true)} />
      )}
      <AppShell />
      {/* Additive: one-time location disclosure, then the device-level request. */}
      <FirstRunLocationConsent />
      <LiveLocationConsentDialog />
      <OnDutyLocationReporter />
    </>
  );
}

